import { useCallback, useEffect, useMemo, useState } from "react";
import {
	getCachedRadius,
	setCachedRadius,
} from "~/features/pharmacy-map/pharmacy-map-cache";
import {
	ANY_RADIUS,
	buildRadiusOptions,
	isPharmacyWithinRadiusKm,
	type PharmacyWithDistance,
	parsePersistedRadius,
	type RadiusOption,
	type RadiusOptionValue,
} from "~/features/pharmacy-map/radius-options";
import { RadiusSelect } from "~/features/pharmacy-map/radius-select.component";
import { resolveEffectiveRadius } from "~/features/pharmacy-map/vendor-filter-map-wrapper.utils";

type UsePdpPharmacyRadiusParams<TItem> = {
	/** Pickup with a resolved address only (Figma 602 "Radius selection"). */
	enabled: boolean;
	pharmacies: readonly TItem[];
	distanceOf: (item: TItem) => PharmacyWithDistance["distance"];
};

type UsePdpPharmacyRadiusResult<TItem> = {
	radius: RadiusOptionValue;
	radiusOptions: RadiusOption[];
	setSelectedRadius: (value: RadiusOptionValue) => void;
	filteredPharmacies: TItem[];
	/** Desktop dropdown chip, or null when the control shouldn't render. */
	radiusControl: React.ReactElement | null;
	/** Bottom-sheet variant for viewports with no separate map surface. */
	mobileRadiusControl: React.ReactElement | null;
};

/**
 * Radius state and the RadiusSelect elements for the PDP pharmacy picker.
 * Reads and writes the same sessionStorage key as the map/list radius
 * control (pharmacy-map-cache.ts), so a radius chosen on one surface is
 * still selected on the other — an existing, deliberate cross-surface
 * contract (MKP-2054), not new state. Builds the control elements here
 * (not in the caller) to keep ProductDetailsVendorSelect under the
 * cognitive-complexity gate.
 */
export function usePdpPharmacyRadius<TItem>({
	enabled,
	pharmacies,
	distanceOf,
}: UsePdpPharmacyRadiusParams<TItem>): UsePdpPharmacyRadiusResult<TItem> {
	const pharmaciesWithDistance = useMemo(
		() => pharmacies.map((item) => ({ distance: distanceOf(item) })),
		[pharmacies, distanceOf],
	);
	const radiusOptions = useMemo(
		() => buildRadiusOptions(pharmaciesWithDistance),
		[pharmaciesWithDistance],
	);
	const [selectedRadius, setSelectedRadiusState] =
		useState<RadiusOptionValue | null>(null);
	const [sessionRadius, setSessionRadius] = useState<RadiusOptionValue | null>(
		null,
	);
	const setSelectedRadius = useCallback((value: RadiusOptionValue) => {
		setSelectedRadiusState(value);
		setCachedRadius(String(value));
	}, []);
	// Read sessionStorage after mount (SSR has no sessionStorage), mirroring
	// use-map-radius-vendors.hook.ts so a radius picked on the map/list is
	// already selected here on first render, not one tick later.
	useEffect(() => {
		setSessionRadius(parsePersistedRadius(getCachedRadius()));
	}, []);
	const radius = resolveEffectiveRadius({
		selectedRadius,
		isVendorsLoading: false,
		radiusOptions,
		sessionRadius,
	});
	const filteredPharmacies = useMemo(() => {
		if (!enabled || radius === ANY_RADIUS) {
			return [...pharmacies];
		}
		return pharmacies.filter((_, index) =>
			isPharmacyWithinRadiusKm(pharmaciesWithDistance[index], radius),
		);
	}, [enabled, pharmacies, pharmaciesWithDistance, radius]);
	const radiusControl = enabled ? (
		<RadiusSelect
			value={radius}
			pharmacyMarkers={pharmaciesWithDistance}
			onChange={setSelectedRadius}
		/>
	) : null;
	const mobileRadiusControl = enabled ? (
		<RadiusSelect
			value={radius}
			pharmacyMarkers={pharmaciesWithDistance}
			onChange={setSelectedRadius}
			variant="sheet"
		/>
	) : null;
	return {
		radius,
		radiusOptions,
		setSelectedRadius,
		filteredPharmacies,
		radiusControl,
		mobileRadiusControl,
	};
}
